import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  LEAVE_REQUEST_STATUS,
  LeaveRequest,
  Student,
  Teacher,
} from "../models";
import { notificationService } from "../services/notification-service";
import { formatLeaveRequestDocument } from "../utils/leave-request-response";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

async function getTeacherStudentIds(
  userId: string,
  schoolId: string,
): Promise<{
  teacher: typeof Teacher.prototype;
  studentIds: Set<string>;
} | null> {
  const teacher = await Teacher.findOne({
    userId: new mongoose.Types.ObjectId(userId),
    schoolId: new mongoose.Types.ObjectId(schoolId),
    isApproved: true,
  });

  if (!teacher) return null;

  const teacherClasses = await Class.find({
    schoolId: new mongoose.Types.ObjectId(schoolId),
    teacherIds: teacher._id,
  }).select("studentIds");

  const studentIds = new Set<string>();
  for (const classItem of teacherClasses) {
    classItem.studentIds?.forEach((id: mongoose.Types.ObjectId) => {
      studentIds.add(id.toString());
    });
  }

  return { teacher, studentIds };
}

class LeaveAppealController {
  async createLeaveAppeal(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;
      const { reason } = req.body;
      const files = (req as Request & { files?: Express.Multer.File[] }).files;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const trimmedReason = String(reason ?? "").trim();
      if (!trimmedReason) {
        res.status(400).json({ success: false, message: "reason is required" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        studentId: student._id,
      });

      if (!leaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.REJECTED) {
        res.status(422).json({
          success: false,
          message: "Appeal can only be submitted for rejected leave requests",
        });
        return;
      }

      if (leaveRequest.appeal?.status === "pending") {
        res.status(409).json({
          success: false,
          message: "An active appeal already exists for this leave request",
        });
        return;
      }

      const documents =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) ?? [];

      await leaveRequest.submitAppeal(trimmedReason, documents);

      const teacherClasses = await Class.find({
        schoolId: leaveRequest.schoolId,
        studentIds: leaveRequest.studentId,
      }).select("teacherIds");
      const teacherIds = new Set<string>();
      for (const c of teacherClasses) {
        c.teacherIds?.forEach((id: mongoose.Types.ObjectId) => {
          teacherIds.add(id.toString());
        });
      }
      const teachers = await Teacher.find({
        _id: {
          $in: [...teacherIds].map(id => new mongoose.Types.ObjectId(id)),
        },
      }).select("userId");
      await notificationService.notifyLeaveAppealSubmitted(
        leaveRequest.id,
        leaveRequest.studentId,
        leaveRequest.schoolId,
        teachers.map(t => t.userId.toString()),
      );

      res.status(201).json({
        success: true,
        message: "Leave appeal submitted successfully",
        data: { leaveRequest: formatLeaveRequestDocument(leaveRequest) },
      } as APIResponse);
    } catch (error) {
      logger.error("Create leave appeal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit leave appeal",
      } as APIResponse);
    }
  }

  async getTeacherLeaveRequestById(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { id } = req.params;

      if (!userId || !schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacherContext = await getTeacherStudentIds(userId, schoolId);
      if (!teacherContext) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        studentId: {
          $in: [...teacherContext.studentIds].map(
            sid => new mongoose.Types.ObjectId(sid),
          ),
        },
      })
        .populate("student", "fullName studentId profilePicture")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email")
        .populate("appeal.reviewedBy", "fullName email");

      if (!leaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }

      res.status(200).json({
        success: true,
        message: "Leave request retrieved successfully",
        data: { leaveRequest: formatLeaveRequestDocument(leaveRequest) },
      } as APIResponse);
    } catch (error) {
      logger.error("Get teacher leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve leave request",
      } as APIResponse);
    }
  }

  async teacherApproveLeaveRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveRequest(req, res, true, "teacher");
  }

  async teacherRejectLeaveRequest(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveRequest(req, res, false, "teacher");
  }

  async teacherApproveLeaveAppeal(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveAppeal(req, res, true, "teacher");
  }

  async teacherRejectLeaveAppeal(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveAppeal(req, res, false, "teacher");
  }

  async schoolApproveLeaveAppeal(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveAppeal(req, res, true, "school");
  }

  async schoolRejectLeaveAppeal(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    await this.reviewLeaveAppeal(req, res, false, "school");
  }

  private async reviewLeaveRequest(
    req: AuthRequest,
    res: Response,
    approve: boolean,
    role: "teacher" | "school",
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { id } = req.params;
      const { notes, rejectionReason } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      let leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!leaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }

      if (role === "teacher") {
        const teacherContext = await getTeacherStudentIds(userId, schoolId);
        if (
          !teacherContext?.studentIds.has(leaveRequest.studentId.toString())
        ) {
          res.status(403).json({ success: false, message: "Access denied" });
          return;
        }
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.PENDING) {
        res.status(409).json({
          success: false,
          message: `Leave request is already ${leaveRequest.status}`,
        });
        return;
      }

      if (approve) {
        await leaveRequest.approve(new mongoose.Types.ObjectId(userId), role);
        await notificationService.notifyLeaveRequestStatus(
          leaveRequest.studentId,
          leaveRequest.schoolId,
          true,
          leaveRequest.id,
          leaveRequest.startDate,
          leaveRequest.endDate,
        );
      } else {
        const reason = String(rejectionReason ?? notes ?? "").trim();
        if (!reason) {
          res.status(400).json({
            success: false,
            message: "rejectionReason is required",
          });
          return;
        }
        await leaveRequest.reject(
          new mongoose.Types.ObjectId(userId),
          reason,
          role,
        );
        await notificationService.notifyLeaveRequestStatus(
          leaveRequest.studentId,
          leaveRequest.schoolId,
          false,
          leaveRequest.id,
          leaveRequest.startDate,
          leaveRequest.endDate,
        );
      }

      const populatedLeaveRequest = await LeaveRequest.findById(
        leaveRequest._id,
      )
        .populate("student", "fullName studentId profilePicture")
        .populate("approvedBy", "fullName email")
        .populate("rejectedBy", "fullName email");

      if (!populatedLeaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }
      leaveRequest = populatedLeaveRequest;

      res.status(200).json({
        success: true,
        message: approve
          ? "Leave request approved successfully"
          : "Leave request rejected successfully",
        data: { leaveRequest: formatLeaveRequestDocument(leaveRequest) },
      } as APIResponse);
    } catch (error) {
      logger.error("Review leave request error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to review leave request",
      } as APIResponse);
    }
  }

  private async reviewLeaveAppeal(
    req: AuthRequest,
    res: Response,
    approve: boolean,
    role: "teacher" | "school",
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { id } = req.params;
      const { reviewNotes, rejectionReason } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      let leaveRequest = await LeaveRequest.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!leaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }

      if (role === "teacher") {
        const teacherContext = await getTeacherStudentIds(userId, schoolId);
        if (
          !teacherContext?.studentIds.has(leaveRequest.studentId.toString())
        ) {
          res.status(403).json({ success: false, message: "Access denied" });
          return;
        }
      }

      if (leaveRequest.status !== LEAVE_REQUEST_STATUS.APPEAL_PENDING) {
        res.status(409).json({
          success: false,
          message: "Leave request is not pending appeal review",
        });
        return;
      }

      if (leaveRequest.appeal?.reviewedAt) {
        res.status(409).json({
          success: false,
          message: "Appeal has already been resolved",
        });
        return;
      }

      if (approve) {
        await leaveRequest.approveAppeal(
          new mongoose.Types.ObjectId(userId),
          role,
          reviewNotes,
        );
        await notificationService.notifyLeaveAppealResolved(
          leaveRequest.studentId,
          leaveRequest.schoolId,
          leaveRequest.id,
          true,
        );
      } else {
        const notes = String(reviewNotes ?? rejectionReason ?? "").trim();
        if (!notes) {
          res.status(400).json({
            success: false,
            message: "reviewNotes is required when rejecting an appeal",
          });
          return;
        }
        await leaveRequest.rejectAppeal(
          new mongoose.Types.ObjectId(userId),
          role,
          notes,
        );
        await notificationService.notifyLeaveAppealResolved(
          leaveRequest.studentId,
          leaveRequest.schoolId,
          leaveRequest.id,
          false,
        );
      }

      const populatedLeaveRequest = await LeaveRequest.findById(
        leaveRequest._id,
      )
        .populate("student", "fullName studentId profilePicture")
        .populate("appeal.reviewedBy", "fullName email");

      if (!populatedLeaveRequest) {
        res
          .status(404)
          .json({ success: false, message: "Leave request not found" });
        return;
      }
      leaveRequest = populatedLeaveRequest;

      res.status(200).json({
        success: true,
        message: approve
          ? "Leave appeal approved successfully"
          : "Leave appeal rejected successfully",
        data: { leaveRequest: formatLeaveRequestDocument(leaveRequest) },
      } as APIResponse);
    } catch (error) {
      logger.error("Review leave appeal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to review leave appeal",
      } as APIResponse);
    }
  }
}

export const leaveAppealController = new LeaveAppealController();
